import { useState } from 'react';
import { Banknote, CreditCard, Smartphone } from 'lucide-react';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import type { PagoVista } from '@shared/types';

/**
 * Sprint 139 — *"Dijo efectivo y pagó con tarjeta."*
 *
 * Reporte del negocio, y ya le había pasado en producción: *"si imprimo el ticket para cobrar en
 * efectivo y luego el cliente decide cambiar a tarjeta, ¿hay manera de cambiar la forma de
 * pago?"*. Hasta hoy no la había, así que la caja quedaba declarando efectivo que nunca entró.
 *
 * ## Por qué esta pantalla es tan corta
 *
 * Porque la decisión ya está tomada cuando se abre: el cliente está enfrente con la tarjeta en la
 * mano. Lo único que hay que contestar es **con qué se pagó** y **por qué cambió**. El importe no
 * se pregunta —no cambia— y por eso no hay un solo campo de dinero editable: la aritmética es la
 * fuente de los errores caros y aquí no hace falta.
 *
 * Toda la cuenta pasa a la forma elegida. El servidor acepta hasta cinco pagos —por si algún día
 * hace falta partir la corrección en dos formas— pero esta pantalla **no lo ofrece**: el caso que
 * reportó el negocio es uno solo ("dijo efectivo, pagó con tarjeta"), y un selector de pago mixto
 * aquí sería una segunda pantalla de cobro con su propia aritmética que mantener.
 */

type Metodo = 'efectivo' | 'tarjeta' | 'transferencia';

const METODOS: Array<{ id: Metodo; etiqueta: string; Icono: typeof Banknote }> = [
  { id: 'efectivo', etiqueta: 'Efectivo', Icono: Banknote },
  { id: 'tarjeta', etiqueta: 'Tarjeta', Icono: CreditCard },
  { id: 'transferencia', etiqueta: 'Transferencia', Icono: Smartphone },
];

interface Props {
  abierto: boolean;
  pedidoId: number;
  /** Lo que está registrado hoy. De aquí salen el total y la propina, que no cambian. */
  pagosActuales: PagoVista[];
  onCerrar: () => void;
  onCorregido: () => void;
}

export function CorregirFormaPagoModal({
  abierto,
  pedidoId,
  pagosActuales,
  onCerrar,
  onCorregido,
}: Props) {
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const total = pagosActuales.reduce((s, p) => s + p.monto, 0);
  const propina = pagosActuales.reduce((s, p) => s + p.propina, 0);

  const [metodo, setMetodo] = useState<Metodo>('tarjeta');
  const [referencia, setReferencia] = useState('');
  const [motivo, setMotivo] = useState('');
  const [pin, setPin] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Sprint 139.1 — La propina, en pesos, como texto.
   *
   * Se siembra con la que ya tenía el cobro: lo normal es no tocarla. Los botones de porcentaje
   * existen porque el caso que la cambia es *"y por la molestia, inclúyame el 10 %"*, dicho en
   * voz alta y con el cliente esperando — nadie calcula el 10 % de $755 de cabeza.
   */
  const [propinaTexto, setPropinaTexto] = useState<string | null>(null);

  if (!abierto) return null;

  /**
   * Sprint 139 — ¿Esta corrección saca dinero de la gaveta?
   *
   * Si lo que estaba registrado era efectivo y lo nuevo no, el efectivo esperado del corte baja:
   * es el único sentido con el que se podría tapar un faltante, y por eso pide el PIN del dueño.
   * Se calcula también aquí para **decirlo antes**, no para decidirlo — quien decide es el
   * servidor, comparando contra los pagos que hay en la base.
   */
  const efectivoPrevio = pagosActuales
    .filter((p) => p.metodo === 'efectivo')
    .reduce((s, p) => s + p.monto + p.propina, 0);

  /** La propina que se va a registrar, en centavos. Sin tocar nada, la que ya tenía. */
  const propinaNueva =
    propinaTexto === null ? propina : Math.round((Number(propinaTexto.replace(',', '.')) || 0) * 100);
  const propinaValida = propinaNueva >= 0 && Number.isFinite(propinaNueva);
  const aCobrar = total + propinaNueva;

  /*
    Se pide el PIN cuando sale dinero: de la caja (el efectivo deja de esperarse) o del bolsillo
    de quien atendió (la propina baja). Subirla no pide nada — ese dinero entra.
  */
  const sacaEfectivo = metodo !== 'efectivo' && efectivoPrevio > 0;
  const bajaPropina = propinaNueva < propina;
  const pideAutorizacion = sacaEfectivo || bajaPropina;

  const registrado = pagosActuales
    .map((p) => `${p.metodo} ${formatPrecio(p.monto, monedaSimbolo, locale)}`)
    .join(' + ');

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    const res = await window.api.pedidos.corregirFormaPago({
      pedidoId,
      // Toda la cuenta pasa a la forma elegida, propina incluida: es el caso real.
      pagos: [
        {
          metodo,
          monto: total,
          propina: propinaNueva,
          referencia: referencia.trim() || undefined,
        },
      ],
      motivo: motivo.trim(),
      supervisorPin: pideAutorizacion ? pin : undefined,
    });
    setGuardando(false);
    if (res.ok) {
      onCorregido();
      onCerrar();
      return;
    }
    setError(traducirErrorBackend(res.error, res.codigo));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <header className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-lg font-semibold">Corregir forma de pago</h2>
          <p className="text-sm text-slate-600">
            Hoy está registrado como <b>{registrado}</b>
            {propina > 0 && <> + propina {formatPrecio(propina, monedaSimbolo, locale)}</>}
          </p>
        </header>

        <div className="space-y-4 px-5 py-4">
          <div>
            <span className="label">¿Con qué pagó al final?</span>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {METODOS.map(({ id, etiqueta, Icono }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMetodo(id)}
                  aria-pressed={metodo === id}
                  className={`flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-lg border-2 text-sm font-semibold ${
                    metodo === id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-slate-300 bg-white text-slate-700'
                  }`}
                >
                  <Icono className="h-5 w-5" />
                  {etiqueta}
                </button>
              ))}
            </div>
          </div>

          {metodo !== 'efectivo' && (
            <div>
              <label className="label" htmlFor="refCorreccion">
                Referencia o últimos 4 dígitos (opcional)
              </label>
              <input
                id="refCorreccion"
                className="input"
                value={referencia}
                maxLength={100}
                onChange={(e) => setReferencia(e.target.value)}
              />
            </div>
          )}

          {/*
            Sprint 139.1 — *"Y por la molestia, inclúyame el 10 %."*

            Con tarjeta la propina se decide **en este momento**, así que aquí tiene que poder
            ponerse. Los porcentajes están porque nadie calcula el 10 % de $755 con el cliente
            esperando, y el total a cobrar se enseña completo: es la cifra que ella va a teclear
            en la terminal.
          */}
          <div>
            <span className="label">Propina</span>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {[0, 10, 15].map((pct) => {
                const valor = Math.round((total * pct) / 100);
                const activo = propinaNueva === valor;
                return (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setPropinaTexto((valor / 100).toFixed(2))}
                    className={`min-h-[44px] rounded-lg border-2 px-4 font-semibold ${
                      activo
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-slate-300 bg-white text-slate-700'
                    }`}
                  >
                    {pct === 0 ? 'Sin propina' : `${pct}%`}
                  </button>
                );
              })}
              <input
                className="input h-11 w-28 text-right"
                inputMode="decimal"
                aria-label="Otra cantidad de propina"
                placeholder="Otra"
                value={propinaTexto ?? (propina / 100).toFixed(2)}
                onChange={(e) => setPropinaTexto(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-lg bg-slate-100 px-3 py-2">
            <div className="flex justify-between text-sm text-slate-700">
              <span>Consumo (no cambia)</span>
              <span className="font-mono">{formatPrecio(total, monedaSimbolo, locale)}</span>
            </div>
            {propinaNueva > 0 && (
              <div className="flex justify-between text-sm text-slate-700">
                <span>Propina</span>
                <span className="font-mono">
                  {formatPrecio(propinaNueva, monedaSimbolo, locale)}
                </span>
              </div>
            )}
            <div className="mt-1 flex justify-between border-t border-slate-300 pt-1 text-base font-bold">
              <span>Se cobra</span>
              <span className="font-mono">{formatPrecio(aCobrar, monedaSimbolo, locale)}</span>
            </div>
          </div>

          <div>
            <label className="label" htmlFor="motivoCorreccion">
              ¿Por qué se corrige?
            </label>
            <input
              id="motivoCorreccion"
              className="input"
              value={motivo}
              maxLength={200}
              placeholder="ej. no le alcanzó el efectivo"
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>

          {/*
            Sprint 139 — El PIN se pide **sólo cuando el dinero sale de la gaveta**, y la pantalla
            dice por qué. Un candado sin explicación se lee como que el sistema desconfía; con la
            razón al lado se lee como lo que es: ese efectivo deja de estar en el corte.
          */}
          {pideAutorizacion && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3">
              <label className="label" htmlFor="pinCorreccion">
                PIN del dueño
              </label>
              <p className="mb-2 text-sm text-amber-900">
                {sacaEfectivo
                  ? `Ese cobro estaba en efectivo: al cambiarlo, ${formatPrecio(
                      efectivoPrevio,
                      monedaSimbolo,
                      locale,
                    )} dejan de contarse en el corte de hoy.`
                  : `Estás bajando la propina de ${formatPrecio(
                      propina,
                      monedaSimbolo,
                      locale,
                    )} a ${formatPrecio(propinaNueva, monedaSimbolo, locale)}: ese dinero es del personal.`}
              </p>
              <input
                id="pinCorreccion"
                className="input w-32 text-center text-2xl tracking-widest"
                inputMode="numeric"
                type="password"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              />
            </div>
          )}

          {/*
            Lo que va a pasar, en una línea y antes de apretar. El total se repite porque es la
            pregunta que nadie hace en voz alta y todos piensan: "¿esto me va a cambiar la cuenta?".
          */}
          <p className="text-sm text-slate-600">
            El consumo no cambia. Queda registrado quién hizo el cambio y por qué, y el cobro
            anterior se guarda como corregido.
          </p>

          {error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
              {error}
            </p>
          )}
        </div>

        <footer className="flex gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button type="button" onClick={onCerrar} className="btn-ghost min-h-[44px] flex-1">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void guardar()}
            disabled={
              guardando ||
              motivo.trim().length < 5 ||
              !propinaValida ||
              (pideAutorizacion && !/^\d{4}$/.test(pin))
            }
            className="btn-primary min-h-[44px] flex-1 disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Corregir'}
          </button>
        </footer>
      </div>
    </div>
  );
}
